import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Download, HelpCircle, Info, Loader2, Search, XCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  acceptancesToCsv, formatDateTime, getAcceptanceStats, listAcceptances, policyKeys,
  type Acceptance, type AcceptanceFilters, type PolicyVersionSummary,
} from '@/lib/policies';

const ALL_VERSIONS = '__all__';

function HashCell({ row }: { row: Acceptance }) {
  if (!row.versionKnown) {
    return <span className="inline-flex items-center gap-1 text-slate-500" title="No published version with this number"><HelpCircle className="h-4 w-4" /> Unknown version</span>;
  }
  return row.hashMatches ? (
    <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-4 w-4" /> Matches</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-red-700" title="The signed hash differs from the published version's hash"><XCircle className="h-4 w-4" /> Mismatch</span>
  );
}

function downloadCsv(filename: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function PolicyConsentRecords({ policyId, versions }: { policyId: string; versions: PolicyVersionSummary[] }) {
  const [version, setVersion] = useState(ALL_VERSIONS);
  const [companyInput, setCompanyInput] = useState('');
  const [filters, setFilters] = useState<AcceptanceFilters>({});

  const stats = useQuery({
    queryKey: policyKeys.stats(policyId),
    queryFn: () => getAcceptanceStats(policyId),
  });

  const records = useInfiniteQuery({
    queryKey: policyKeys.acceptances(policyId, filters),
    queryFn: ({ pageParam }) => listAcceptances(policyId, filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: last => last.nextCursor,
  });

  const rows = records.data?.pages.flatMap(p => p.items) ?? [];
  const firstPage = records.data?.pages[0];

  const applyFilters = (nextVersion = version) => setFilters({
    version: nextVersion === ALL_VERSIONS ? undefined : nextVersion,
    companyId: companyInput.trim() || undefined,
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-500">All acceptances</CardTitle></CardHeader>
          <CardContent className="text-2xl font-semibold">
            {stats.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats.data?.total.toLocaleString() ?? '—'}
          </CardContent>
        </Card>
        {stats.data?.byVersion.slice(0, 3).map(v => (
          <Card key={v.version}>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-500">Version {v.version}</CardTitle></CardHeader>
            <CardContent className="text-2xl font-semibold">{v.count.toLocaleString()}</CardContent>
          </Card>
        ))}
      </div>

      {firstPage?.ordering === 'documentId' && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Records are listed by record ID, not by date</AlertTitle>
          <AlertDescription>
            Newest-first ordering needs a Firestore index on this collection.{' '}
            {firstPage.indexUrl ? (
              <a href={firstPage.indexUrl} target="_blank" rel="noreferrer" className="underline">Create the index in the Firebase console</a>
            ) : 'Ask the Firebase project owner to create it (see POLICY_MANAGEMENT.md).'}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-end gap-3 space-y-0">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="consent-version">Version</label>
            <Select value={version} onValueChange={v => { setVersion(v); applyFilters(v); }}>
              <SelectTrigger id="consent-version" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VERSIONS}>All versions</SelectItem>
                {versions.map(v => <SelectItem key={v.version} value={v.version}>{v.version}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <form
            className="flex items-end gap-2"
            onSubmit={e => { e.preventDefault(); applyFilters(); }}
          >
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="consent-company">Company ID</label>
              <Input id="consent-company" value={companyInput} onChange={e => setCompanyInput(e.target.value)} className="w-56" placeholder="Exact company ID" />
            </div>
            <Button type="submit" variant="outline"><Search className="h-4 w-4" /></Button>
          </form>
          <Button
            variant="outline"
            className="ml-auto"
            disabled={rows.length === 0}
            onClick={() => downloadCsv(`${policyId}-consent-records.csv`, acceptancesToCsv(rows))}
          >
            <Download className="h-4 w-4 mr-2" /> Export {rows.length} loaded
          </Button>
        </CardHeader>
        <CardContent>
          {records.isLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : records.error ? (
            <p className="py-8 text-center text-red-600">Could not load consent records: {(records.error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-slate-500">No consent records match.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Accepted</TableHead>
                    <TableHead>User ID</TableHead>
                    <TableHead>Company ID</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Signed hash</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap">{formatDateTime(row.acceptedAt)}</TableCell>
                      <TableCell className="font-mono text-xs">{row.userId}</TableCell>
                      <TableCell className="font-mono text-xs">{row.companyId || '—'}</TableCell>
                      <TableCell>{row.acceptedVersion}</TableCell>
                      <TableCell>{row.acceptanceMethod ?? '—'}</TableCell>
                      <TableCell>{[row.platform, row.appVersion].filter(Boolean).join(' ') || '—'}</TableCell>
                      <TableCell><HashCell row={row} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {records.hasNextPage && (
                <div className="pt-4 flex justify-center">
                  <Button variant="outline" onClick={() => records.fetchNextPage()} disabled={records.isFetchingNextPage}>
                    {records.isFetchingNextPage && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
